import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LayerRadar from "@/components/n360/LayerRadar";
import { GradeBadge, AccessBadge, VerifiedTag, ConfidencePill } from "@/components/n360/Badges";
import { ACCESS_STYLE, DOSSIER_SCHEMA } from "@/lib/content";

export default function ProfileView() {
  const { profileId } = useParams();
  const { meta } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("summary");
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    api.get(`/profile/${profileId}`).then(({ data }) => setP(data)).catch(() => toast.error("Profile not found"));
  }, [profileId]);

  const layers = meta?.layers || [];
  const pillarName = (id) => (meta?.pillars || []).find((x) => x.id === id)?.name || id;

  const connect = async () => {
    setConnecting(true);
    try {
      await api.post("/connections/request", { recipient_id: profileId });
      toast.success("Bilateral corridor request dispatched.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setConnecting(false); }
  };

  if (!p) return <div className="text-center py-24 text-[#8598a0] rise">Retrieving dossier…</div>;

  const modules = {};
  (p.facts || []).forEach((f) => { (modules[f.module] = modules[f.module] || []).push(f); });

  return (
    <div className="rise">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#5f7d86] hover:text-[#F1551D] mb-5" data-testid="profile-back-btn"><Icons.ArrowLeft className="w-4 h-4" /> Back</button>

      {/* Header */}
      <div className="n360-card p-7 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-24 h-24 rounded-2xl overflow-hidden bg-[#F6EEDC] border border-[#F1551D]/25 shrink-0">
            {p.avatar_url ? <img src={p.avatar_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-3xl text-[#F1551D] font-display">{p.name?.[0]}</div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl sm:text-3xl text-[#0D3C4C]">{p.name}</h1>
              <VerifiedTag verified={p.verified} />
            </div>
            <p className="text-[#2f5561] mt-1">{p.headline}</p>
            <div className="text-sm text-[#8598a0] mt-1">{p.organization} · {p.country} · {pillarName(p.pillar)}{p.sub_sector ? ` — ${p.sub_sector}` : ""}</div>
            <div className="flex flex-wrap gap-2 mt-3">
              <GradeBadge grade={p.grade} />
              <AccessBadge level={p.access_level} />
            </div>
          </div>
          {!p.is_owner ? (
            <button onClick={connect} disabled={connecting} data-testid="connect-wire-request-btn" className="px-6 py-2.5 rounded-full bg-[#F1551D] text-[#FEF7E7] font-medium hover:bg-[#F0A315] transition-colors disabled:opacity-60 self-start">
              {connecting ? "Sending…" : "Request Corridor"}
            </button>
          ) : (
            <button onClick={() => navigate("/app/profile")} data-testid="edit-own-profile-btn" className="px-6 py-2.5 rounded-full border border-[#F1551D]/40 text-[#0D3C4C] hover:bg-[#F1551D]/10 self-start">Edit Dossier</button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {[["summary", "Summary"], ["intelligence", "8-Layer Intelligence"], ["dossier", "Dossier Facts"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`profile-dossier-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${tab === k ? "bg-[#F1551D]/15 text-[#B84214] border border-[#F1551D]/40" : "text-[#5f7d86] border border-transparent hover:text-[#0D3C4C]"}`}>{l}</button>
        ))}
      </div>

      {tab === "summary" && (
        <div className="grid md:grid-cols-2 gap-5">
          {p.redacted_count > 0 && (
            <div className={`md:col-span-2 flex items-center gap-3 px-4 py-3 rounded-lg border ${ACCESS_STYLE.Confidential}`} data-testid="summary-redaction-notice">
              <Icons.Lock className="w-4 h-4" />
              <span className="text-sm">{p.redacted_count} intelligence fact(s) are beyond your current clearance and remain redacted.</span>
            </div>
          )}
          <div className="n360-card p-6">
            <h3 className="font-display text-lg text-[#0D3C4C] mb-3">Executive Biography</h3>
            <p className="text-sm text-[#2f5561] leading-relaxed">{p.bio || "No biography provided."}</p>
          </div>
          <div className="n360-card p-6">
            <h3 className="font-display text-lg text-[#0D3C4C] mb-3">Opportunity Corridors</h3>
            <div className="mb-3">
              <div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Seeking</div>
              <div className="flex flex-wrap gap-1.5">{(p.seeking || []).length ? p.seeking.map((s) => <span key={s} className="text-xs px-2 py-1 rounded-full border border-emerald-500/30 text-emerald-300">{s}</span>) : <span className="text-[#aab9bd] text-sm">—</span>}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#8598a0] mb-1.5">Offering</div>
              <div className="flex flex-wrap gap-1.5">{(p.offering || []).length ? p.offering.map((s) => <span key={s} className="text-xs px-2 py-1 rounded-full border border-[#F1551D]/30 text-[#B84214]">{s}</span>) : <span className="text-[#aab9bd] text-sm">—</span>}</div>
            </div>
          </div>
        </div>
      )}

      {tab === "intelligence" && (
        <div className="n360-card p-6">
          <div className="grid lg:grid-cols-2 gap-6 items-center">
            <LayerRadar layers={layers} scores={p.layer_scores || {}} height={340} />
            <div className="space-y-2">
              {layers.map((l) => (
                <div key={l.id} className="flex items-center gap-3">
                  <span className="font-mono-i text-[10px] text-[#F1551D] w-14">{l.code}</span>
                  <span className="text-sm text-[#2f5561] flex-1 truncate">{l.name}</span>
                  <div className="w-28 h-1.5 rounded-full bg-[#F6EEDC] overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#F1551D] to-[#F0A315]" style={{ width: `${p.layer_scores?.[l.id] || 0}%` }} />
                  </div>
                  <span className="font-mono-i text-xs text-[#5f7d86] w-8 text-right">{p.layer_scores?.[l.id] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "dossier" && (
        <div className="space-y-5">
          {p.redacted_count > 0 && (
            <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${ACCESS_STYLE.Confidential}`} data-testid="redaction-notice">
              <Icons.Lock className="w-4 h-4" />
              <span className="text-sm">{p.redacted_count} fact(s) redacted — beyond your current clearance level.</span>
            </div>
          )}
          {(() => {
            const factMap = {};
            (p.facts || []).forEach((f) => { factMap[(f.label || "").toLowerCase().trim()] = f; });
            return (
              <div className="grid md:grid-cols-2 gap-5">
                {DOSSIER_SCHEMA.map((m) => {
                  const Icon = Icons[m.icon] || Icons.FileText;
                  return (
                    <div key={m.id} data-testid={`dossier-module-${m.id}`} className={`n360-card overflow-hidden ${m.wide ? "md:col-span-2" : ""}`}>
                      <div className="flex items-center gap-3 px-5 py-3" style={{ background: m.color }}>
                        <span className="w-8 h-8 rounded-full bg-white/15 grid place-items-center text-white text-xs font-mono-i">{m.no}</span>
                        <Icon className="w-5 h-5 text-white/90 shrink-0" />
                        <h3 className="font-display text-white text-sm sm:text-base tracking-wide leading-tight">{m.title}</h3>
                      </div>
                      <div className="p-5 space-y-4">
                        {m.groups.map((g, gi) => (
                          <div key={gi}>
                            {g.title && <div className="text-[11px] uppercase tracking-wider font-semibold text-[#F1551D] mb-2">{g.title}</div>}
                            <div className={`grid gap-2 ${m.wide ? "sm:grid-cols-2 lg:grid-cols-3" : "sm:grid-cols-2"}`}>
                              {g.fields.map((field) => {
                                const fact = factMap[field.toLowerCase()];
                                return (
                                  <div key={field} className={`rounded-lg border px-3 py-2 ${fact ? "border-emerald-300 bg-emerald-50/60" : "border-[#EADFC7] bg-[#FEF7E7]/40"}`}>
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-[10px] uppercase tracking-wide text-[#8598a0] leading-tight">{field}</span>
                                      {fact ? <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" /> : null}
                                    </div>
                                    {fact ? (
                                      <>
                                        <div className="text-sm text-[#154859] mt-0.5 leading-snug">{fact.value}</div>
                                        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-[#8598a0]"><Icons.FileSearch className="w-3 h-3" />{fact.source || "—"} · {fact.confidence}%</div>
                                      </>
                                    ) : (
                                      <div className="text-sm text-[#c3d0d4] mt-0.5">—</div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
